import { forwardRef, useImperativeHandle, useRef, useState, useLayoutEffect } from "react";
import { cn } from "@/lib/utils";
import { listTemplateTokens } from "@/lib/messageVariables";

export interface VariableTextEditorHandle {
  /** Insert text at the caret (or append when the field was never focused). */
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

interface VariableTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
  /** Names the editor accepts without flagging them as unknown. */
  knownVariables?: string[];
}

/**
 * A plain textarea layered over a synced highlight surface. Typing, paste and
 * caret behaviour stay native (a contenteditable token editor breaks all three);
 * the layer underneath only paints `{variable}` tokens so they stop reading as
 * raw text.
 */
export const VariableTextEditor = forwardRef<VariableTextEditorHandle, VariableTextEditorProps>(
  ({ value, onChange, placeholder, disabled, maxLength, className, knownVariables = [] }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const highlightRef = useRef<HTMLDivElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      insertAtCursor: (text: string) => {
        const el = textareaRef.current;
        if (!el) {
          onChange(maxLength ? (value + text).slice(0, maxLength) : value + text);
          return;
        }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + text + value.slice(end);
        onChange(maxLength ? next.slice(0, maxLength) : next);
        requestAnimationFrame(() => {
          el.focus();
          const caret = Math.min(start + text.length, maxLength ?? Number.MAX_SAFE_INTEGER);
          el.setSelectionRange(caret, caret);
        });
      },
    }));

    useLayoutEffect(() => {
      if (highlightRef.current) highlightRef.current.scrollTop = scrollTop;
    }, [scrollTop, value]);

    const known = new Set(knownVariables);

    // Build the painted layer: plain text plus a span for every token.
    const segments: Array<{ text: string; tone?: "basic" | "advanced" | "warn" | "unknown" }> = [];
    let cursor = 0;
    for (const token of listTemplateTokens(value)) {
      if (token.start > cursor) segments.push({ text: value.slice(cursor, token.start) });
      const tone = token.isEnrichment
        ? "advanced"
        : known.size === 0 || known.has(token.name)
          ? "basic"
          : "unknown";
      segments.push({ text: token.raw, tone });
      cursor = token.end;
    }
    if (cursor < value.length) segments.push({ text: value.slice(cursor) });

    const toneClass = (tone?: string) => {
      switch (tone) {
        case "basic":
          return "rounded bg-success/15 text-success ring-1 ring-success/25";
        case "advanced":
          return "rounded bg-primary/15 text-primary ring-1 ring-primary/25";
        case "warn":
          return "rounded bg-warning/25 text-warning ring-1 ring-warning/40";
        case "unknown":
          return "rounded bg-destructive/15 text-destructive ring-1 ring-destructive/30";
        default:
          return "";
      }
    };

    const shared =
      "w-full whitespace-pre-wrap break-words px-3 py-2 text-sm leading-relaxed font-sans";

    return (
      <div
        className={cn(
          "relative rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
          disabled && "opacity-60",
          className
        )}
      >
        <div
          ref={highlightRef}
          aria-hidden
          className={cn(shared, "pointer-events-none absolute inset-0 overflow-hidden")}
        >
          {segments.map((seg, i) => (
            <span key={i} className={toneClass(seg.tone)}>
              {seg.text}
            </span>
          ))}
          {/* Trailing newline needs height so the caret line stays aligned. */}
          {value.endsWith("\n") && <span> </span>}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          onScroll={(e) => setScrollTop((e.target as HTMLTextAreaElement).scrollTop)}
          className={cn(
            shared,
            "relative min-h-[120px] resize-y bg-transparent text-transparent caret-foreground outline-none placeholder:text-muted-foreground",
            "selection:bg-primary/25 selection:text-transparent"
          )}
        />
      </div>
    );
  }
);

VariableTextEditor.displayName = "VariableTextEditor";
